import { t } from '../../i18next';
import {
  IndustrializationId,
  getIndusIdForRejection,
  makeOrBuyAndHtzCheck,
  setIndustrialization,
} from './businessLogic';

export type MakeOrBuyInputType = {
  bestellNummer?: string;
  debitorName?: string;
  designOwnerId?: number;
  htzTypeId?: number;
  khkPkey?: number;
  lumpSumDue?: Date;
  makeOrBuy: number;
  makeOrBuyModified?: boolean;
  noveltyId?: number;
  npiAcceptPO?: number;
  npiAsKenner?: string;
  npiComment?: string;
  nrpDue?: Date;
  pagDeliveryDateDue?: Date;
  pagFollowUpDate?: Date;
  pagIsPartOf?: string;
  poKubu?: string;
  preHtz?: string;
  preHtzManufacturedBy?: string;
  projectId?: number;
  rpDue?: Date;
  sheet1Htz?: string;
};

export type RulesResultType = {
  data?: { table: string; fields: Map<string, boolean | number | string> }[];
  error?: string;
  save: boolean;
};

export function makeOrBuyRules(inputData: MakeOrBuyInputType): RulesResultType {
  const { makeOrBuy } = inputData;

  if ([1, 16, 17, 18, 19].includes(makeOrBuy)) {
    return { save: true };
  }

  if ([3, 4, 5, 6, 7, 8, 9, 10, 11, 15, 20, 21].includes(makeOrBuy)) {
    return makeOrBuyDecided(inputData);
  }

  if (makeOrBuy === 2) {
    return reIndustrialize(inputData);
  }

  if (makeOrBuy === 14) {
    return industrializationOnHold(inputData);
  }

  if ([12, 13].includes(makeOrBuy)) {
    return makeOrBuyRejected(inputData);
  }
}

function makeOrBuyRejected(inputData: MakeOrBuyInputType): RulesResultType {
  const { makeOrBuy, makeOrBuyModified, npiComment, preHtz, preHtzManufacturedBy, sheet1Htz } =
    inputData;

  const result = { save: true, data: null };

  if (!npiComment || npiComment.trim().length === 0) {
    return { save: false, error: t('mob.CommentRejectReason') };
  }

  if (makeOrBuyAndHtzCheck(makeOrBuyModified, sheet1Htz)) {
    return result;
  }

  const industrializationId: IndustrializationId = getIndusIdForRejection(makeOrBuy);
  result.data = [
    setIndustrialization({ industrializationId, makeOrBuy, preHtz, preHtzManufacturedBy }),
  ];

  return result;
}

function reIndustrialize(inputData: MakeOrBuyInputType): RulesResultType {
  const { makeOrBuy, makeOrBuyModified, preHtz, preHtzManufacturedBy, sheet1Htz } = inputData;

  const result: RulesResultType = { save: true, data: [] };

  if (makeOrBuyAndHtzCheck(makeOrBuyModified, sheet1Htz)) {
    return result;
  }

  return {
    save: true,
    data: [
      setIndustrialization({
        industrializationId: IndustrializationId.Industrialize,
        makeOrBuy,
        preHtz,
        preHtzManufacturedBy,
      }),
    ],
  };
}

function makeOrBuyDecided(inputData: MakeOrBuyInputType): RulesResultType {
  const {
    designOwnerId,
    htzTypeId,
    makeOrBuy,
    makeOrBuyModified,
    pagIsPartOf,
    preHtz,
    preHtzManufacturedBy,
    projectId,
    sheet1Htz,
  } = inputData;

  if (!pagIsPartOf && designOwnerId === 2 && projectId !== 10 && htzTypeId === 5) {
    return { save: false, error: t('mob.MakeOrBuyThirdMandatory') };
  }

  if (makeOrBuyAndHtzCheck(makeOrBuyModified, sheet1Htz)) {
    return { save: true };
  }

  if (makeOrBuyModified === true && sheet1Htz !== null) {
    return {
      save: true,
      data: [
        setIndustrialization({
          industrializationId: IndustrializationId.Industrialize,
          makeOrBuy,
          preHtz,
          preHtzManufacturedBy,
        }),
      ],
    };
  } else {
    return { save: true };
  }
}

function industrializationOnHold(inputData: MakeOrBuyInputType): RulesResultType {
  const { preHtz, makeOrBuy, makeOrBuyModified, pagFollowUpDate, preHtzManufacturedBy, sheet1Htz } =
    inputData;

  if (!pagFollowUpDate) {
    return { save: false, error: t('mob.SetFollowUpDate') };
  }

  if (makeOrBuyAndHtzCheck(makeOrBuyModified, sheet1Htz)) {
    return { save: true };
  }

  if (makeOrBuyModified === true && sheet1Htz !== null) {
    return {
      save: true,
      data: [
        setIndustrialization({
          industrializationId: IndustrializationId.IndustrializationOnHold,
          makeOrBuy,
          preHtz,
          preHtzManufacturedBy,
        }),
      ],
    };
  }
}
